import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { Sidebar } from "./components/layout/sidebar";
import { CommandBar } from "./components/layout/command-bar";
import { ThemeProvider } from "./contexts/theme-context";
import { AlertProvider } from "./contexts/alert-context";
import { AuthProvider, useAuthContext } from "./contexts/auth-context";
import { WorkspaceProvider, useWorkspace } from "./contexts/workspace-context";
import { UsersProvider } from "./contexts/users-context";
import { LabelsProvider } from "./contexts/labels-context";
import { queryClient } from "./lib/react-query";
import { Toaster } from "./components/ui/toaster";
import { LoadingScreen } from "./components/ui/loading-screen";
import React, { lazy, Suspense } from "react";

const MyWorkPage = lazy(() =>
  import("./pages/my-work").then((module) => ({ default: module.MyWorkPage })),
);
const DigestPage = lazy(() =>
  import("./pages/digest").then((module) => ({ default: module.DigestPage })),
);
const ProductivityPage = lazy(() =>
  import("./pages/productivity").then((module) => ({
    default: module.ProductivityPage,
  })),
);
const TasksPage = lazy(() =>
  import("./pages/tasks").then((module) => ({ default: module.TasksPage })),
);
const TaskDetailPage = lazy(() => import("./pages/task-detail"));
const ViewsPage = lazy(() =>
  import("./pages/views").then((module) => ({ default: module.ViewsPage })),
);
const ThemesPage = lazy(() =>
  import("./pages/themes").then((module) => ({ default: module.ThemesPage })),
);
const ThemeDetailPage = lazy(() => import("./pages/theme-detail"));
const FeaturesPage = lazy(() =>
  import("./pages/features").then((module) => ({ default: module.FeaturesPage })),
);
const FeatureDetailPage = lazy(() => import("./pages/feature-detail"));
const CyclesPage = lazy(() =>
  import("./pages/cycles").then((module) => ({ default: module.CyclesPage })),
);
const CycleDetailPage = lazy(() =>
  import("./pages/cycle-detail").then((module) => ({
    default: module.CycleDetailPage,
  })),
);
const SignInPage = lazy(() =>
  import("./pages/sign-in").then((module) => ({ default: module.SignInPage })),
);
const SignUpPage = lazy(() =>
  import("./pages/sign-up").then((module) => ({ default: module.SignUpPage })),
);
const OnboardingPage = lazy(() =>
  import("./pages/onboarding").then((module) => ({
    default: module.OnboardingPage,
  })),
);
const Integrations = lazy(() => import("./pages/integrations"));
const IntegrationDetail = lazy(() => import("./pages/integration-detail"));
const WorkspaceSettingsPage = lazy(() => import("./pages/workspace-settings"));
const ProfilePage = lazy(() =>
  import("./pages/profile").then((module) => ({ default: module.ProfilePage })),
);
const SettingsPage = lazy(() =>
  import("./pages/settings").then((module) => ({ default: module.SettingsPage })),
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuthContext();

  if (loading) {
    return <LoadingScreen message="Authenticating" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace />;
  }

  return <>{children}</>;
}

/**
 * WorkspaceGate - Shows onboarding if user has no workspaces
 * This ensures new users create a workspace before using the app
 */
function WorkspaceGate({ children }: { children: React.ReactNode }) {
  const { workspaces, loading } = useWorkspace();

  // Show loading while fetching workspaces
  if (loading) {
    return <LoadingScreen message="Loading workspaces" />;
  }

  // If user has no workspaces, show onboarding
  if (workspaces.length === 0) {
    return <OnboardingPage />;
  }

  // User has workspaces, show the app
  return <>{children}</>;
}

function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <WorkspaceGate>
        <div className="flex h-screen overflow-hidden bg-background">
          <Sidebar />
          <main className="flex-1 flex flex-col overflow-hidden">
            <Outlet />
          </main>
          <CommandBar />
        </div>
      </WorkspaceGate>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AlertProvider>
          <AuthProvider>
              <BrowserRouter>
                <WorkspaceProvider>
                  {/* Users and Labels providers depend on WorkspaceProvider */}
                  <UsersProvider>
                    <LabelsProvider>
                      <Suspense fallback={<LoadingScreen message="Loading" />}>
                      <Routes>
                        {/* Auth routes - with wildcard for SSO callback */}
                        <Route path="/sign-in/*" element={<SignInPage />} />
                        <Route path="/sign-up/*" element={<SignUpPage />} />

                        {/* Protected routes with shared layout */}
                        <Route element={<ProtectedLayout />}>
                          <Route path="/my-work" element={<MyWorkPage />} />
                          <Route path="/digest" element={<DigestPage />} />
                          <Route path="/productivity" element={<ProductivityPage />} />
                          <Route path="/tasks" element={<TasksPage />} />
                          <Route path="/tasks/:taskId" element={<TaskDetailPage />} />
                          <Route path="/themes/:workspaceId" element={<ThemesPage />} />
                          <Route path="/themes/:workspaceId/:themeId" element={<ThemeDetailPage />} />
                          <Route path="/features/:workspaceId" element={<FeaturesPage />} />
                          <Route path="/features/:workspaceId/:featureId" element={<FeatureDetailPage />} />
                          <Route path="/cycles/:workspaceId" element={<CyclesPage />} />
                          <Route path="/cycles/:workspaceId/:cycleId" element={<CycleDetailPage />} />
                          <Route path="/views" element={<ViewsPage />} />
                          <Route path="/integrations" element={<Integrations />} />
                          <Route path="/integrations/:integrationId" element={<IntegrationDetail />} />
                          <Route path="/settings/:workspaceId" element={<WorkspaceSettingsPage />} />
                          <Route path="/profile" element={<ProfilePage />} />
                          <Route path="/settings" element={<SettingsPage />} />
                        </Route>

                        {/* Default redirect */}
                        <Route path="/" element={<Navigate to="/my-work" replace />} />
                      </Routes>
                      </Suspense>
                      <Toaster />
                    </LabelsProvider>
                  </UsersProvider>
                </WorkspaceProvider>
              </BrowserRouter>
          </AuthProvider>
        </AlertProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
